/**
 * Resolve the hex a model3d visualization block should tint to from the
 * customer's chosen option, given a configurator schema + catalog + config.
 *
 * The model3d viz block carries a declarative ``color_field`` (a configurator
 * field key). That field is a ``choice`` bound to a material
 * option-source property (``options_source.material_property``); each option in the
 * SELECTED material's ``option_sources`` carries the ``hex`` — the single source of
 * truth (no hardcoded colour table). This helper walks every option field and maps
 * ``fieldKey → resolved hex`` for whatever the customer (live) or the snapshot
 * (persisted) currently has chosen.
 *
 * The InteractiveViewer then looks the block's ``color_field`` up in this map; an
 * unbound block, an unselected option, or an option without a hex leaves the key
 * absent so the viewer falls back to the block's static ``color`` / its default.
 *
 * Pure + catalog-driven so the live configurator and the persisted quote/order
 * preview resolve identically.
 */
import type { Catalog } from "@/api/types";

interface RawField {
  key?: unknown;
  options_source?: unknown;
}

/** ``options_source.material_property`` → property key, when a ``choice`` field is
 *  bound to a per-material option list (else ``undefined``). Mirrors
 *  ``CustomerConfiguratorForm.boundOptionPropertyOf``. */
export function optionPropertyOf(field: RawField): string | undefined {
  const src = field.options_source;
  if (!src || typeof src !== "object" || Array.isArray(src)) return undefined;
  const key = (src as { material_property?: unknown }).material_property;
  return typeof key === "string" && key.trim() ? key : undefined;
}

export function resolveBoundColors(
  schema: Record<string, unknown> | null | undefined,
  catalog: Catalog | null | undefined,
  materialCode: string | null | undefined,
  config: Record<string, unknown>,
): Record<string, string> {
  const fields = (schema as { fields?: unknown } | null | undefined)?.fields;
  if (!Array.isArray(fields)) return {};
  const material = materialCode
    ? (catalog?.materials ?? []).find((m) => m.code === materialCode)
    : undefined;
  const out: Record<string, string> = {};
  for (const raw of fields as RawField[]) {
    const property = optionPropertyOf(raw);
    const key = typeof raw.key === "string" ? raw.key : "";
    if (!property || !key) continue;
    const chosen = config[key];
    if (typeof chosen !== "string" || !chosen) continue;
    const option = material?.option_sources?.[property]?.find((o) => o.code === chosen);
    if (option?.hex) out[key] = option.hex;
  }
  return out;
}
